import { motion } from "framer-motion";
import { useEnquiry } from "@/components/site/EnquiryProvider";

function GalleryItem({ art, i }) {
  const { openEnquiry } = useEnquiry();
  return (
    <motion.button
      type="button"
      onClick={() => openEnquiry(art)}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.7, delay: (i % 3) * 0.06 }}
      data-testid={`sacred-item-${art.slug}`}
      data-cursor="Enquire"
      className="group mb-4 lg:mb-6 block w-full break-inside-avoid text-left"
    >
      <div className="relative overflow-hidden bg-sand gallery-shadow">
        <img
          src={art.image}
          alt={art.title}
          loading="lazy"
          className="w-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.06]"
        />
        <div className="absolute inset-0 flex items-end bg-gradient-to-t from-ink/90 via-ink/10 to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100">
          <div className="p-4">
            <p className="font-serif text-lg text-paper leading-tight">{art.title}</p>
            <p className="font-body text-[10px] tracking-[0.18em] uppercase text-ochre mt-1">
              {art.medium} &middot; Enquire
            </p>
          </div>
        </div>
      </div>
    </motion.button>
  );
}

export default function SacredWild({ artworks = [] }) {
  const paintings = artworks.filter((a) => a.kind === "painting");
  const photos = artworks.filter((a) => a.kind === "photograph");

  return (
    <section
      id="sacred-wild"
      className="relative py-24 md:py-36 bg-paper overflow-hidden"
      data-testid="sacred-section"
    >
      <div className="max-w-screen-2xl mx-auto px-6 md:px-10">
        <div className="grid md:grid-cols-12 gap-8 items-end mb-16 md:mb-24">
          <div className="md:col-span-8">
            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="font-body text-[11px] tracking-[0.35em] uppercase text-ochre mb-5"
            >
              New Series &mdash; Solo Exhibition
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
              className="font-serif text-4xl sm:text-5xl lg:text-7xl leading-[1] text-ink tracking-tight"
            >
              The <span className="italic text-ochre">Sacred</span> Wild
            </motion.h2>
          </div>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.15 }}
            className="md:col-span-4 font-body text-base text-ink-soft leading-relaxed"
          >
            Temple architecture, pomegranates, birds and sacred flora &mdash; a body of
            work where South India&apos;s pillars and courtyards become inhabited,
            living spaces. Select any work to enquire.
          </motion.p>
        </div>

        {/* Paintings */}
        <div className="columns-1 sm:columns-2 lg:columns-3 gap-4 lg:gap-6">
          {paintings.map((art, i) => (
            <GalleryItem key={art.slug} art={art} i={i} />
          ))}
        </div>

        {/* Photographs */}
        {photos.length > 0 && (
          <>
            <div className="mt-20 md:mt-28 mb-12 flex items-center gap-6">
              <span className="font-serif italic text-2xl md:text-3xl text-ink whitespace-nowrap">
                From the journey
              </span>
              <span className="h-px flex-1 bg-ink-soft/25" />
              <span className="font-body text-[11px] tracking-[0.25em] uppercase text-ink-soft whitespace-nowrap">
                South India &middot; Photographs
              </span>
            </div>
            <div className="columns-2 lg:columns-4 gap-4 lg:gap-6">
              {photos.map((art, i) => (
                <GalleryItem key={art.slug} art={art} i={i} />
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
}
